import classNames from 'classnames'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { NotificationController } from 'core-components/library/popup'

import { validateByMIMEType } from './helpers/validateByMIMEType'

import styles from './DragNDrop.module.scss'

interface IDragNDrop {
  className?: string
  browseClick: number
  uploadFile: (e: File[]) => void
  children: ReactNode
  enablePointerEvents?: boolean
  disableBorder?: boolean
  multifiles?: boolean
  acceptedFormats?: string
  disabled?: boolean
  /**
   * Called with the files that don't match `acceptedFormats`, instead of the default error
   * notification. Accepted files from the same drop / selection still go to `uploadFile`
   */
  onRejectFiles?: (rejectedFiles: File[]) => void
}

export const DragNDrop = ({
  className,
  browseClick,
  uploadFile,
  children,
  enablePointerEvents,
  disableBorder,
  multifiles,
  acceptedFormats,
  disabled,
  onRejectFiles,
}: IDragNDrop) => {
  const browseRef = useRef<HTMLInputElement>(null)
  const [drag, setDrag] = useState(false)

  useEffect(() => {
    if (browseClick) {
      browseRef.current?.click()
    }
  }, [browseClick])

  const handleDrag = (e: any, isDrag: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setDrag(isDrag)
    }
  }

  const onLoadHandler = (files: File[]) => {
    if (disabled || files.length === 0) {
      return
    }

    if (acceptedFormats) {
      const { wrongFilesByType, acceptedFiles } = validateByMIMEType(files, acceptedFormats)

      if (wrongFilesByType.length && onRejectFiles) {
        onRejectFiles(wrongFilesByType)

        if (acceptedFiles.length) {
          uploadFile(acceptedFiles)
        }

        return
      }

      if (wrongFilesByType.length) {
        const unacceptableFilesName = wrongFilesByType.map((file) => file.name).join(', ')
        const message = !acceptedFiles.length
          ? `File extensions do not match valid format.<br> Allowed format: "${acceptedFormats}"`
          : `Allowed file type format: ${acceptedFormats}.<br>
             Unacceptable files: ${unacceptableFilesName}`

        return NotificationController.showError({ message })
      }
    }

    uploadFile(files)
  }

  return (
    <div
      data-testid={'dnd-root'}
      className={classNames(
        styles.root,
        drag ? styles['root--drag'] : '',
        disableBorder ? styles['root--no-border'] : '',
        className || ''
      )}
      onDragEnter={(e) => handleDrag(e, true)}
      onDragLeave={(e) => handleDrag(e, false)}
      onDrop={(e) => {
        handleDrag(e, false)
        if (e.dataTransfer.files.length) {
          onLoadHandler([...e.dataTransfer.files])
        }
      }}
      onDragOver={(e) => handleDrag(e, true)}
    >
      <input
        ref={browseRef}
        data-testid={'dnd-input'}
        className={styles.input}
        type={'file'}
        accept={acceptedFormats}
        multiple={multifiles}
        onChange={(e) => {
          if (e.target.files?.length) {
            onLoadHandler([...e.target.files])
          }
          if (browseRef.current) {
            browseRef.current.value = ''
          }
        }}
      />
      <div
        className={enablePointerEvents ? '' : styles.noPointerEvents}
        data-testid={'dnd-pointer-events'}
      >
        {children}
      </div>
    </div>
  )
}
